/**
 * 会话数据初始化 Hook
 *
 * 职责：
 * 1. 加载会话基础信息（thread info）
 * 2. 加载关联的项目信息（project info）
 * 3. 加载会话的历史消息列表
 * 4. 检查并恢复活跃的 Agent Run 状态
 * 5. 设置初始的评分 Agent Run ID
 * 6. 处理加载过程中的错误和 loading 状态
 * 7. 加载完成后自动滚动到消息底部
 * 8. 组件卸载时清理所有相关 store 数据
 *
 * 使用场景：
 * - 会话页面首次加载时
 * - threadId 变化时重新初始化
 * - 组件卸载时自动清理数据
 */
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { getThread, getProject, getMessages } from '@/services/thread';
import { mapApiMessagesToMessages } from '@/lib/thread';
import { useStores } from '@/store/hooks/useStore';
import { AgentRun } from '@/lib/api';
import i18n from '@/i18n/config';

interface UseThreadInitializationParams {
  threadId: string;
  setAgentRunId: (id: string | null) => void;
  setRateAgentRunId: (id: string | null) => void;
  scrollToBottom: (behavior?: 'auto' | 'smooth') => void;
  // TODO 合并代码，最小改动
  checkAgentRuns: () => Promise<any>;
  checkLastTaskIsRunning: (agentRun: AgentRun) => Promise<boolean>;
}

interface UseThreadInitializationReturn {
  loading: boolean;
  error: string | null;
}

export const useThreadInitialization = ({
  threadId,
  setAgentRunId,
  setRateAgentRunId,
  scrollToBottom,
  checkAgentRuns,
  checkLastTaskIsRunning,
}: UseThreadInitializationParams): UseThreadInitializationReturn => {
  const router = useRouter();
  const stores = useStores();
  const {
    setThreadInfo,
    setMessages,
    setThreadStatus,
    reset: resetThreadStore,
  } = stores.threadStore;
  const { setProject, reset: resetProjectStore } = stores.threadProjectStore;
  const { reset: resetToolStore } = stores.threadToolStore;
  const { reset: resetWorkspaceStore } = stores.threadWorkspaceStore;
  const { reset: resetTemplateStore } = stores.threadTemplateStore;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (!threadId) {
          throw new Error(i18n.t('common:error.sessionIdCannotBeEmpty'));
        }

        // 1. 加载会话信息
        const threadData = await getThread(threadId);

        if (!threadData) {
          // 跳转到 dashboard 页面
          router.replace('/dashboard');
          throw new Error(i18n.t('common:error.getSessionInfoFailed'));
        }

        setThreadInfo(threadData);

        // 2. 加载项目信息
        if (threadData?.project_id) {
          const projectData = await getProject(threadData.project_id);
          if (projectData) {
            setProject(projectData);
          }
        }

        // 3. 加载消息列表
        const messagesData = await getMessages(threadId);
        const messages = mapApiMessagesToMessages(messagesData || []);
        setMessages(messages);

        // 4. 滚动到底部
        setTimeout(() => {
          scrollToBottom('auto');
        }, 500);

        // 5. 检查活跃的 agent runs
        try {
          console.log('[PAGE] Checking for active agent runs...');

          const agentRuns = await checkAgentRuns();

          // 查找正在运行的 agent run
          const activeRun = agentRuns.find((run) => run.status === 'running');

          if (activeRun) {
            setAgentRunId(activeRun.id);
          }
        } catch (err) {
          console.error('[PAGE] Error checking agent runs:', err);
        }
      } catch (err) {
        console.error('获取会话信息失败', err);
        const errorMessage =
          err instanceof Error
            ? err.message
            : i18n.t('common:error.getSessionInfoFailed');
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [threadId]);

  // 组件卸载时清理所有 store 数据
  useEffect(() => {
    return () => {
      console.log('[useThreadInitialization] Cleaning up stores on unmount');
      resetProjectStore();
      resetThreadStore();
      resetToolStore();
      resetWorkspaceStore();
      resetTemplateStore();
    };
  }, []);

  return {
    loading,
    error,
  };
};
